// src/hooks/useMessaging.ts
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store/store";
import {
  apiPostReadMessage,
  fetchAllChats,
  fetchChat,
  sendPostMessage,
} from "../api/messages";
import {
  addNewMessage,
  addOrUpdateChat,
  appendMessages,
  ChartHeader,
  Chat,
  clearChatUnreadCount,
  markUnreadChats,
  Message,
  setChartHeader,
  setChat,
  setCurrentPage,
  setHasMoreMessages,
  setIsLoadingMessages,
  setMessages,
  setShowChatMessages,
  updateChatMessage,
} from "../store/messageSlice";
import { useLoading } from "./useLoading";
import { useNotificationService } from "./notification";

// Define the receiver data type
interface ReceiverData {
  id: number;
  name: string;
  image: string;
}

const messageStructure = (
  receiverInfo: { receiver_id: number; receiver_name: string },
  senderInfo: { sender_id: number; sender_name: string },
  message: string,
  messageId: number
) => ({
  id: messageId,
  sender_id: senderInfo.sender_id,
  sender_name: senderInfo.sender_name,
  receiver_id: receiverInfo.receiver_id,
  receiver_name: receiverInfo.receiver_name,
  message: message,
  sent_at: Date().toLocaleString(),
  read_at: null,
  is_read: false,
  edited_at: Date().toLocaleString(),
});

export const useMessaging = () => {
  const dispatch = useDispatch();
  const chartHeader = useSelector(
    (state: RootState) => state.message.chartHeader
  );
  const chatMainId = useSelector((state: RootState) => state.message.chartId);
  const account_profile = useSelector(
    (state: RootState) => state.content.account_profile_data
  );
  const { startLoading, stopLoading } = useLoading();
  // loading state for fetching chart
  const [loading, setLoading] = useState(false);

  const { showNotification } = useNotificationService();

  const messages = useSelector((state: RootState) => state.message.messages);
  const hasMoreMessages = useSelector(
    (state: RootState) => state.message.hasMoreMessages
  );
  const currentPage = useSelector(
    (state: RootState) => state.message.currentPage
  );
  const chats = useSelector((state: RootState) => state.message.chats);
  const isLoadingMessages = useSelector(
    (state: RootState) => state.message.isLoadingMessages
  );

  // Get unread messages from Redux
  const unreadMessages = useSelector(
    (state: RootState) => state.message.unreadMessages
  );

  const startNewChat = (receiver_data: ReceiverData) => {
    const chatKey = [account_profile?.id, receiver_data.id].sort().join("-");
    const newChat: Chat = {
      chatKey: chatKey,
      id: receiver_data.id,
      last_message: "",
      user_id: receiver_data.id,
      message: "",
      unread_count: 0,
      last_message_sent_at: "",
      unread_messages_count: 0,
      photo_url: receiver_data.image, // Only add if this matches your Chat type
      name: receiver_data.name, // Only add if this matches your Chat type
    };

    dispatch(addOrUpdateChat(newChat));
  };

  // Fetch all chats
  const fetchChats = async () => {
    try {
      setLoading(true);
      const response = await fetchAllChats();

      if (Array.isArray(response)) {
        dispatch(setChat(response));
      }

      // Identify and highlight unread chats
      const unreadChats = response.filter((chat: Chat) =>
        unreadMessages.some(
          (unreadMsg: Message) => unreadMsg.sender_id === chat.user_id
        )
      );

      // Optional: Dispatch to Redux to mark unread chats
      dispatch(markUnreadChats(unreadChats));
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch chats", error);
      showNotification("Charts data not available", "warning");
      setLoading(false);
    }
  };

  const selectChat = async (chatId: number) => {
    // empty chat data before fetching
    dispatch(setMessages([]));

    // clear chat unread message count
    dispatch(
      clearChatUnreadCount({
        userId: account_profile.id, // The ID of the current user
        chatUserId: chatId, // The ID of the user whose chat was opened
      })
    );
    try {
      startLoading();

      // First, update loading state
      dispatch(setIsLoadingMessages(true));

      // Then fetch the initial messages
      const params = {
        page: 1,
        page_size: 20,
        chat_id: chatId,
      };

      const response = await fetchChat(params);

      // Once we have the response, we can safely reset the state
      dispatch(setMessages(response));
      dispatch(setHasMoreMessages(response.length >= 20));
      dispatch(setCurrentPage(1));
      dispatch(setShowChatMessages(true));
      // Mark messages as read
      await apiPostReadMessage(chatId);

      // Update selected chat
      const selectedChatData = chats.find((chat) => chat.id === chatId);
    } catch (error) {
      console.error("Failed to select chat", error);
      // Reset messages state on error
      dispatch(setMessages([]));
    } finally {
      dispatch(setIsLoadingMessages(false));
      stopLoading();
    }
  };

  const fetchPaginatedMessages = async (
    chatId: number,
    page: number,
    pageSize: number = 20
  ) => {
    // Don't fetch if we're already loading or know there are no more messages
    // But DO allow fetching if this is page 1 (initial load)
    if ((page !== 1 && !hasMoreMessages) || isLoadingMessages) return;
    
    dispatch(setIsLoadingMessages(true));

    try {
      const params = {
        page,
        page_size: pageSize,
        chat_id: chatId,
      };

      const response = await fetchChat(params);

      // Update hasMoreMessages based on response size
      dispatch(setHasMoreMessages(response.length >= pageSize));

      // Update messages based on page number
      if (page === 1) {
        dispatch(setMessages(response));
      } else {
        dispatch(appendMessages(response));
      }

      dispatch(setCurrentPage(page));
    } catch (error) {
      console.error("Failed to fetch messages", error);
    } finally {
      
    }
  };

  const updateChartHeader = (chatHeader: ChartHeader) => {
    dispatch(setChartHeader(chatHeader));
  };

  // Modified sendMessage to use Redux
  const sendMessage = async (receiverId: number, content: string) => {
    startLoading();
    dispatch(setIsLoadingMessages(false));
    try {
      // Get the last message ID
      const lastMessage = messages[messages.length - 1]; // Redux state
      const newMessageId = lastMessage ? lastMessage.id + 1 : 1;

      const newMessage = messageStructure(
        { receiver_id: receiverId, receiver_name: chartHeader.name },
        { sender_id: account_profile.id, sender_name: account_profile.name },
        content,
        newMessageId // Ensure ID is sequential
      );

      // Optimistically update messages in Redux
      dispatch(addNewMessage(newMessage));

      const response = await sendPostMessage({
        receiver_id: receiverId,
        message: content,
      });

      if (!response) {
        showNotification("Message was not sent!", "error");
      }
      // Mark messages as read
      await apiPostReadMessage(chatMainId);
      dispatch(
        updateChatMessage({
          sender_id: account_profile?.id,
          receiver_id: receiverId,
          message: content,
          last_message_sent_at: Date.now(),
          sender_name: account_profile.name,
        })
      );
    } catch (error) {
      console.error("Failed to send message", error);
    } finally {
      stopLoading();
    }
  };

  return {
    messages,
    startNewChat,
    hasMoreMessages,
    isLoadingMessages,
    updateChartHeader,
    selectChat,
    sendMessage,
    fetchChats,
    loading,
    fetchPaginatedMessages,
  };
};
